import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import WorkflowsAPI from '../../api/workflows';

export const state = {
  records: [],
  currentWorkflow: null,
  availableConfig: null,
  executions: [],
  executionsMeta: {},
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchingConfig: false,
    isFetchingExecutions: false,
    isTesting: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getWorkflows(_state) {
    return _state.records;
  },
  getById: _state => id => {
    return _state.records.find(w => w.id === Number(id));
  },
  getCurrentWorkflow(_state) {
    return _state.currentWorkflow;
  },
  getAvailableConfig(_state) {
    return _state.availableConfig;
  },
  getExecutions(_state) {
    return _state.executions;
  },
  getExecutionsMeta(_state) {
    return _state.executionsMeta;
  },
};

export const actions = {
  get: async function getWorkflows({ commit }) {
    commit(types.SET_WORKFLOW_UI_FLAG, { isFetching: true });
    try {
      const response = await WorkflowsAPI.get();
      commit(types.SET_WORKFLOWS, response.data.payload || response.data);
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_WORKFLOW_UI_FLAG, { isFetching: false });
    }
  },
  show: async function showWorkflow({ commit }, id) {
    commit(types.SET_WORKFLOW_UI_FLAG, { isFetchingItem: true });
    try {
      const response = await WorkflowsAPI.show(id);
      commit(types.SET_CURRENT_WORKFLOW, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_WORKFLOW_UI_FLAG, { isFetchingItem: false });
    }
  },
  create: async function createWorkflow({ commit }, workflowObj) {
    commit(types.SET_WORKFLOW_UI_FLAG, { isCreating: true });
    try {
      const response = await WorkflowsAPI.create(workflowObj);
      commit(types.ADD_WORKFLOW, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_WORKFLOW_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_WORKFLOW_UI_FLAG, { isUpdating: true });
    try {
      const response = await WorkflowsAPI.update(id, updateObj);
      commit(types.EDIT_WORKFLOW, response.data);
      commit(types.SET_CURRENT_WORKFLOW, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_WORKFLOW_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_WORKFLOW_UI_FLAG, { isDeleting: true });
    try {
      await WorkflowsAPI.delete(id);
      commit(types.DELETE_WORKFLOW, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_WORKFLOW_UI_FLAG, { isDeleting: false });
    }
  },
  clone: async ({ commit }, id) => {
    commit(types.SET_WORKFLOW_UI_FLAG, { isCreating: true });
    try {
      const response = await WorkflowsAPI.clone(id);
      commit(types.ADD_WORKFLOW, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_WORKFLOW_UI_FLAG, { isCreating: false });
    }
  },
  toggle: async ({ commit }, id) => {
    commit(types.SET_WORKFLOW_UI_FLAG, { isUpdating: true });
    try {
      const response = await WorkflowsAPI.toggle(id);
      commit(types.EDIT_WORKFLOW, response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_WORKFLOW_UI_FLAG, { isUpdating: false });
    }
  },
  test: async ({ commit }, id) => {
    commit(types.SET_WORKFLOW_UI_FLAG, { isTesting: true });
    try {
      const response = await WorkflowsAPI.test(id);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_WORKFLOW_UI_FLAG, { isTesting: false });
    }
  },
  fetchAvailableConfig: async function fetchAvailableConfig({ commit }) {
    commit(types.SET_WORKFLOW_UI_FLAG, { isFetchingConfig: true });
    try {
      const response = await WorkflowsAPI.availableConfig();
      commit(types.SET_WORKFLOW_AVAILABLE_CONFIG, response.data);
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_WORKFLOW_UI_FLAG, { isFetchingConfig: false });
    }
  },
  fetchExecutions: async function fetchExecutions(
    { commit },
    { workflowId, page = 1, status = '' }
  ) {
    commit(types.SET_WORKFLOW_UI_FLAG, { isFetchingExecutions: true });
    try {
      const params = { page, per_page: 25 };
      if (status) params.status = status;
      const response = await WorkflowsAPI.executions(workflowId, params);
      commit(
        types.SET_WORKFLOW_EXECUTIONS,
        response.data.payload || response.data
      );
      if (response.data.meta) {
        commit(types.SET_WORKFLOW_EXECUTIONS_META, response.data.meta);
      }
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_WORKFLOW_UI_FLAG, { isFetchingExecutions: false });
    }
  },
};

export const mutations = {
  [types.SET_WORKFLOW_UI_FLAG](_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },
  [types.SET_WORKFLOWS]: MutationHelpers.set,
  [types.ADD_WORKFLOW]: MutationHelpers.create,
  [types.EDIT_WORKFLOW]: MutationHelpers.update,
  [types.DELETE_WORKFLOW]: MutationHelpers.destroy,
  [types.SET_CURRENT_WORKFLOW](_state, workflow) {
    _state.currentWorkflow = workflow;
  },
  [types.SET_WORKFLOW_AVAILABLE_CONFIG](_state, config) {
    _state.availableConfig = config;
  },
  [types.SET_WORKFLOW_EXECUTIONS](_state, executions) {
    _state.executions = executions;
  },
  [types.SET_WORKFLOW_EXECUTIONS_META](_state, meta) {
    _state.executionsMeta = meta;
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
